export type Suit = 'spades' | 'hearts' | 'diamonds' | 'clubs';
export type Rank = '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A' | '2' | '3' | '4' | '5';

export interface Card {
  suit: Suit;
  rank: Rank;
  id: string; // unique identifier e.g. "H-10"
}

export const SUITS: Suit[] = ['spades', 'hearts', 'diamonds', 'clubs'];

// Ranks ordered from low to high. For 36-card decks we use 6..A; for 52-card we use 2..A.
export const RANKS_36: Rank[] = ['6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
export const RANKS_52: Rank[] = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

export const SUIT_SYMBOL: Record<Suit, string> = {
  spades: '♠',
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
};

export const SUIT_COLOR: Record<Suit, 'red' | 'black'> = {
  spades: 'black',
  clubs: 'black',
  hearts: 'red',
  diamonds: 'red',
};

export function rankIndex(rank: Rank, deckSize: 36 | 52): number {
  const ranks = deckSize === 36 ? RANKS_36 : RANKS_52;
  return ranks.indexOf(rank);
}

export function cardId(card: Pick<Card, 'suit' | 'rank'>): string {
  return `${card.suit[0].toUpperCase()}-${card.rank}`;
}

export function buildDeck(deckSize: 36 | 52): Card[] {
  const ranks = deckSize === 36 ? RANKS_36 : RANKS_52;
  const deck: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of ranks) {
      deck.push({ suit, rank, id: cardId({ suit, rank }) });
    }
  }
  return deck;
}

/** Standard Durak "does the defense card beat the attack card?" rule. */
export function beats(attack: Card, defense: Card, trumpSuit: Suit | null, deckSize: 36 | 52): boolean {
  if (defense.suit === attack.suit) {
    return rankIndex(defense.rank, deckSize) > rankIndex(attack.rank, deckSize);
  }
  return trumpSuit !== null && defense.suit === trumpSuit && attack.suit !== trumpSuit;
}

export function shuffleDeck<T>(deck: T[], rng: () => number = Math.random): T[] {
  const a = [...deck];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
